import React from 'react';
import { inject, observer } from 'mobx-react';
import './coachingSessionListPane.module.scss';
import { CalendarView } from '../../molecules/calendar/CalendarView';
import { DateGroup } from '../../molecules/reservable/DateGroup';
import { ScheduleBadge, ScheduleListItem } from '../../molecules/reservable/ScheduleListItem';
import { groupByDate, ReservableType } from '../../../../commonv3/types/reservable';
import { BackButton } from '../../../../commonv3/components/atoms/buttons/BackButton';
import moment from 'moment-timezone';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import {
  isInitialCoachingSession,
  isRecentlyPublished,
  isRegularCoachingSession,
  isSelfCoachingSession,
  SessionIndexView,
  SessionTemplateKey,
  SessionType,
} from '../../../types/sessions';
import { HomePageState } from '../../../containers/home/states/HomePageState';
import { observable, makeObservable } from 'mobx';
import { LoaderRow } from '../../atoms/layout/LoaderRow';
import { getLocationSelector, getOnlineSelector, LocationSelector } from '../../molecules/forms/LocationSelector';
import { CheckBoxCardNoBorder } from '../../atoms/select/CheckBoxCardNoBorder';
import { WaitlistToggleButton } from './MokumokuSessionListPane';
import { SessionWaitlistState } from './states/SessionWaitlistState';
import { SessionWaitlistDescription } from '../../molecules/session_waitlists/SessionWaitlistDescription';
import { SelectInput } from '../../../../commonv3/components/atoms/forms/primitive/SelectInput';
import { CoachingPageState } from '../../../containers/coaching/states/CoachingPageState';
import CoachingGuideBanner from './images/coaching_guide_banner.png';

type Props = {
  homeState: HomePageState | CoachingPageState;
};

const MaskMessage = ({
  templateKey,
  reservableCoachingTypes,
  reservedRegularCoachingWithinCurrentMonth,
  reservedTwoRegularCoaching,
}: {
  templateKey: SessionTemplateKey;
  reservableCoachingTypes: string[];
  reservedRegularCoachingWithinCurrentMonth: boolean;
  reservedTwoRegularCoaching: boolean;
}): React.ReactElement | null => {
  if (reservedTwoRegularCoaching) {
    return <span styleName="pink">グループコーチングは2枠までしか予約できません</span>;
  }
  if (reservedRegularCoachingWithinCurrentMonth) {
    return <span styleName="pink">グループコーチングは同じ月に1枠までしか予約できません</span>;
  }
  if (reservableCoachingTypes.includes(templateKey)) {
    return null;
  }
  switch (templateKey) {
    case SessionTemplateKey.ONLINE_INITIAL_GROUP_COACHING_V3_SECOND:
    case SessionTemplateKey.OFFLINE_INITIAL_GROUP_COACHING_V3_SECOND:
      return (
        <span styleName="pink">
          初回コーチングワークショップSTEP2は
          <br />
          STEP1を完了すると予約することができます
        </span>
      );
    case SessionTemplateKey.ONLINE_INITIAL_GROUP_COACHING_V2_SECOND:
    case SessionTemplateKey.OFFLINE_INITIAL_GROUP_COACHING_V2_SECOND:
      return (
        <span styleName="pink">
          初回コーチングSTEP2「ゴール設定編」は
          <br />
          STEP1「価値観ワーク編」を完了すると
          <br />
          予約することができます
        </span>
      );
    case SessionTemplateKey.ONLINE_REGULAR_GROUP_COACHING:
    case SessionTemplateKey.OFFLINE_REGULAR_GROUP_COACHING:
    case SessionTemplateKey.ONLINE_SELF_COACHING:
      return (
        <span>
          グループコーチング/セルフコーチングもくもく会はWelcomeワークショップを完了すると予約することができます
        </span>
      );
    default:
      return null;
  }
};

const IntroductionOf1on1Coaching = () => {
  return (
    <div styleName="intro">
      <div styleName="intro-title">1on1 コーチング</div>
      <div styleName="intro-description">
        1on1コーチングとは、コーチとの1対1での対話を通して深い内省が得られる個人セッションです。ご希望の方はご活用ください。
      </div>
      <div styleName="intro-detail">
        <div>初回価格：5,000円 / 通常価格：10,000円</div>
        <div>所用時間：60分</div>
      </div>
      <div styleName="external-button-wrapper">
        <Button
          styleName="external-button"
          type="primary"
          size="s"
          href="https://reserva.be/shelikes_1on1coaching"
          target="_blank"
        >
          ご予約ページはこちら
        </Button>
      </div>
      <div styleName="intro-memo">※外部の予約システムのページに遷移します。</div>
    </div>
  );
};

export const CoachingSessionListPane = inject('homeState')(
  observer(
    class CoachingSessionListPane extends React.Component<Props> {
      onlyInitialCoaching = true;
      onlyRegularCoaching = true;
      onlyRecentlyPublished = false;
      onlySelfCoaching = true;
      sessionWaitlistState = new SessionWaitlistState();

      constructor(props: Props) {
        super(props);

        makeObservable(this, {
          onlyInitialCoaching: observable,
          onlyRegularCoaching: observable,
          onlyRecentlyPublished: observable,
          onlySelfCoaching: observable,
          sessionWaitlistState: observable,
        });
      }

      async componentDidMount() {
        const { sessionList } = this.props.homeState;
        const { homeLocation } = GlobalStatesV3.currentUser;
        await homeLocation.reload();
        const { isHomeLocationSet, isCurrentLocationOnline, locationId } = homeLocation;
        sessionList.setSessionType(SessionType.GROUP_COACHING);

        await this.props.homeState.reloadLocations();
        await this.sessionWaitlistState.reloadSessionWaitlists();
        sessionList.setSelectedLocationSelectors(
          isHomeLocationSet && !isCurrentLocationOnline
            ? [getLocationSelector(locationId), getOnlineSelector()]
            : [getOnlineSelector()]
        );
        const [onlyInitialCoaching, onlyRegularCoaching, onlyRecentlyPublished] =
          sessionList.updateSessionListByQueryString(location.search);
        this.onlyInitialCoaching = onlyInitialCoaching;
        this.onlyRegularCoaching = onlyRegularCoaching;
        this.onlyRecentlyPublished = onlyRecentlyPublished;
      }

      render() {
        const { locations, reservationList, sessionList } = this.props.homeState;
        const {
          sessionsOfType,
          filteredSessions,
          selectedOnlyOpen,
          selectedLocationSelectors,
          setSelectedLocationSelectors,
          selectedMultipleDates,
          setSelectedDate,
          setSelectedMonth,
          setSelectedOnlyOpen,
          sessionListLoading,
          groupCoachingCoaches,
          selectedGroupCoachingCoachId,
          setSelectedGroupCoachingCoachId,
        } = sessionList;
        const {
          isInitialCoachingVisible,
          reservableCoachingTypes,
          reservedRegularCoachingCounts,
          visibleCoachingTypes,
        } = reservationList;
        const extraFilter = (s: SessionIndexView) => {
          if (!visibleCoachingTypes.includes(s.template_key)) {
            return false;
          }
          if (this.onlyRecentlyPublished && !isRecentlyPublished(s.published_at)) return false;

          const isInitialSession = this.onlyInitialCoaching && isInitialCoachingSession(s.template_key);
          const isRegularSession = this.onlyRegularCoaching && isRegularCoachingSession(s.template_key);
          const isSelfSession = this.onlySelfCoaching && isSelfCoachingSession(s.template_key);
          return isInitialSession || isRegularSession || isSelfSession;
        };
        const groups = groupByDate(filteredSessions.filter(extraFilter), (r) => r.starts_at);
        const { availableTokens, loading: currentUserLoading } = GlobalStatesV3.currentUser;
        const { homeLocation } = GlobalStatesV3.currentUser;
        const { isHomeLocationSet, locationId: homeLocationId } = homeLocation;
        const loading = sessionListLoading || currentUserLoading || reservationList.loading || homeLocation.loading;
        const { currentUser } = GlobalStatesV3;
        const is1on1CoachingAvailableUser =
          reservableCoachingTypes.includes(SessionTemplateKey.ONLINE_REGULAR_GROUP_COACHING) &&
          reservableCoachingTypes.includes(SessionTemplateKey.OFFLINE_REGULAR_GROUP_COACHING) &&
          currentUser.currentUser.days_since_join >= 30;
        const reservedTwoRegularCoaching = reservedRegularCoachingCounts.length === 2;

        return (
          <div id="coaching-session-list-pane" styleName="pane">
            {GlobalStatesV3.viewport.isMobile && <BackButton />}

            <div styleName="pane-header">
              <div styleName="pane-title">コーチングの予約</div>
              <div styleName="pane-header-border" />
              <SessionWaitlistDescription />
            </div>
            <div styleName="views">
              <div styleName="calendar-wrapper">
                <CalendarView
                  startDate={moment().date(1)}
                  endDate={moment().add(6, 'month').date(1)}
                  selectedMultipleDates={selectedMultipleDates}
                  clickables={sessionsOfType.filter(extraFilter).map((r) => r.starts_at)}
                  highlights={sessionsOfType
                    .filter(extraFilter)
                    .filter((s) => isInitialCoachingSession(s.template_key))
                    .map((r) => moment(r.starts_at).startOf('day'))}
                  onClick={setSelectedDate}
                  onMonthChange={setSelectedMonth}
                />
                {is1on1CoachingAvailableUser && <IntroductionOf1on1Coaching />}
              </div>
              <div styleName="schedule-list-wrapper">
                <div styleName="coaching-guide-banner">
                  <a
                    href="https://shelikes.jp/home?redirect_url=https://sheinc.notion.site/SHElikes-90a961bd21fc4199bad6008a82cc30a5"
                    target="_blank"
                  >
                    <img src={CoachingGuideBanner} styleName="coaching-guide-banner-img" />
                  </a>
                </div>
                <div id="coaching-schedule-list-header" styleName="schedule-list-header">
                  <div styleName="schedule-list-header-box">
                    <div styleName="schedule-list-header-text">表示する拠点</div>
                    <LocationSelector
                      locations={locations.getOrElse([])}
                      homeLocationId={homeLocationId}
                      isHomeLocationSet={isHomeLocationSet}
                      showOnline={true}
                      showOnlineFirst={true}
                      showOther={false}
                      locationSelectors={selectedLocationSelectors}
                      onChange={setSelectedLocationSelectors}
                    />
                  </div>
                  <div styleName="schedule-list-header-box" id="coaching-type-selector">
                    <div styleName="schedule-list-header-text">種類</div>
                    <div styleName="initial-filter">
                      {isInitialCoachingVisible && (
                        <CheckBoxCardNoBorder
                          checked={this.onlyInitialCoaching}
                          label="Welcomeワークショップ"
                          onCheck={(checked) => (this.onlyInitialCoaching = checked)}
                        />
                      )}
                      <CheckBoxCardNoBorder
                        checked={this.onlyRegularCoaching}
                        label="グループコーチング"
                        onCheck={(checked) => (this.onlyRegularCoaching = checked)}
                      />
                      <CheckBoxCardNoBorder
                        checked={this.onlySelfCoaching}
                        label="セルフコーチングもくもく会"
                        onCheck={(checked) => (this.onlySelfCoaching = checked)}
                      />
                    </div>
                  </div>

                  <div styleName="schedule-list-header-box">
                    <div styleName="schedule-list-header-text">空き状況</div>
                    <CheckBoxCardNoBorder
                      id="selectedOnlyOpen"
                      checked={selectedOnlyOpen}
                      label="空き枠のみ"
                      onCheck={(checked) => setSelectedOnlyOpen(checked)}
                    />
                  </div>
                  <div>
                    <div styleName="schedule-list-header-text">コーチ</div>
                    <SelectInput
                      options={(groupCoachingCoaches.value || []).map((coach) => {
                        return {
                          value: `${coach.id}`,
                          label: coach.name,
                        };
                      })}
                      placeholder="未選択"
                      onChange={(v) => setSelectedGroupCoachingCoachId(v)}
                      value={selectedGroupCoachingCoachId}
                      serializer={(v) => String(v)}
                      deserializer={(v) => String(v)}
                      styleName="select-input"
                    />
                  </div>
                </div>
                <div styleName="schedule-list-body">
                  {sessionListLoading && <LoaderRow />}
                  {groups.map((group) => (
                    <DateGroup key={group.date} date={group.date}>
                      {group.entries.map((entry) => {
                        const reserved = reservationList.isReserved(ReservableType.SESSION, entry.id);
                        const full = entry.active_reservation_count >= entry.capacity;
                        const initialCoaching = isInitialCoachingSession(entry.template_key);
                        const isNotReservableCoachingType = !reservableCoachingTypes.includes(entry.template_key);
                        const yearMonthOfCoachingSessionStartsAt = moment(entry.starts_at).format('YYYYMM');
                        const reservedRegularCoachingWithinCurrentMonth =
                          reservedRegularCoachingCounts.find(
                            (counts) =>
                              moment(counts.month).format('YYYYMM') === yearMonthOfCoachingSessionStartsAt &&
                              counts.count > 0
                          ) !== undefined;

                        const isReservable = availableTokens.isAffordable(
                          entry.required_token_type,
                          entry.required_token_amount,
                          entry.starts_at
                        );
                        const isSelfCoachingSession = entry.template_key === SessionTemplateKey.ONLINE_SELF_COACHING;
                        const isRegularCoachingSession =
                          entry.template_key === SessionTemplateKey.ONLINE_REGULAR_GROUP_COACHING ||
                          entry.template_key === SessionTemplateKey.OFFLINE_REGULAR_GROUP_COACHING;
                        // reservableCoachingTypes.lengthが0となるのは初回コーチングを予約したがまだ出席していない場合のみ
                        const isAttendedInitialCoaching =
                          reservableCoachingTypes.length !== 0 &&
                          !reservableCoachingTypes.some((type) => type.includes('initial'));
                        const sessionReservationPath = V3Routes.coachingSessionReservation(entry.id);
                        return (
                          <ScheduleListItem
                            key={entry.id}
                            title={entry.template_name}
                            reservable={entry}
                            actionItem={
                              <div styleName="action-items">
                                <Button
                                  id="session-reserve-button"
                                  styleName="button"
                                  type="primary"
                                  loading={currentUserLoading}
                                  disabled={
                                    /*
                                  判別対象の枠と同月に通常コーチングを予約している場合
                                  reservedRegularCoachingWithinCurrentMonthがtrueとなりボタンがdisabledになってしまうが、
                                  セルフコーチングもくもく会は通常コーチングの予約状態に関係なく常に予約できるようにしたい。
                                  そのため、判別対象の枠がセルフコーチングもくもく会であれば純粋にその枠を予約しているかどうかでdisabledの状態が切り替変わるようにしている。
                                  ただし、セルフコーチングもくもく会は初回コーチングに出席済の場合のみ予約が可能。
                                  ※ 判別条件が複雑になり過ぎているので今後リファクタしたい
                                */
                                    (isAttendedInitialCoaching && isSelfCoachingSession && reserved) ||
                                    isNotReservableCoachingType ||
                                    (isRegularCoachingSession && reservedRegularCoachingWithinCurrentMonth) ||
                                    !isReservable ||
                                    reserved ||
                                    full
                                  }
                                  size={GlobalStatesV3.viewport.isMobile ? 'm' : 'xs'}
                                  onClick={() => GlobalStatesV3.routing.push(sessionReservationPath)}
                                >
                                  {reserved ? '予約済' : full ? '満席' : '選択する'}
                                </Button>
                                {isReservable && (
                                  <WaitlistToggleButton
                                    entry={entry}
                                    reserved={reserved}
                                    sessionWaitlistState={this.sessionWaitlistState}
                                  />
                                )}
                              </div>
                            }
                            titleBadge={initialCoaching ? <ScheduleBadge color="pink">初回</ScheduleBadge> : null}
                            masked={
                              isNotReservableCoachingType ||
                              (isRegularCoachingSession && reservedRegularCoachingWithinCurrentMonth) ||
                              (isRegularCoachingSession && reservedTwoRegularCoaching)
                            }
                            maskMessage={
                              <MaskMessage
                                templateKey={entry.template_key}
                                reservableCoachingTypes={reservableCoachingTypes}
                                reservedRegularCoachingWithinCurrentMonth={reservedRegularCoachingWithinCurrentMonth}
                                reservedTwoRegularCoaching={reservedTwoRegularCoaching}
                              />
                            }
                          />
                        );
                      })}
                    </DateGroup>
                  ))}
                </div>
              </div>
            </div>
            {loading && <div id="loading-indicator" />}
          </div>
        );
      }
    }
  )
);
